/* UTILS */
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';

import type { CampaignUsersQueryParams } from '@/api/calls/campaign/campaign.types';
import type { ContactsQueryParams } from '@/api/calls/contacts/contacts.types';
/* API */
import {
  patchUser,
  patchUserData,
  postRequestRestartUserPmVoice,
  postRequestUserPmVoice,
  postRetakeUserPmVoice,
  postUserResults,
  postUserRetakeTest,
  postUserWorkflowReminder
} from '@/api/calls/user/user.api';
import Reporter from '@/api/clients/reporter/reporter';
/* HELPERS */
import { displayGenericErrorToaster, displayGenericSuccessToaster } from '@/api/helpers/helpers';
import { t } from '@/config/i18n';
import { HttpStatus } from '@/enums/httpStatus';
import { handleBatchResponse } from '@/helpers/batches.helpers';
import { getFullnameOrEmail } from '@/helpers/users';
/* STORES */
import { useGlobalStore } from '@/stores/global';
/* TYPES */
import type { PatchUserParams } from '@/types/api/user/user';
import type { BaseUser, UserData } from '@/types/users/users';

/* PATCH */
/**
 * Update a user data - can be given as params any data that can be updated on the user - when new data can be updated, add to the dedicated type
 * @REMARK check how to handle invalidation when the case presents itself - on the component depending on what data is being patched or here
 */
export function usePatchUserMutation() {
  return useMutation({
    mutationFn: ({ uuid, params }: { uuid: string; params: Partial<PatchUserParams> }) => patchUser(uuid, params),
    onSuccess: (_response, context) => {
      if (context.params.locale_id) {
        // If patch includes the user's local, reload page to update i18n
        window.location.reload();
      }
    },
    onError: () => displayGenericErrorToaster()
  });
}

/* PATCH */
/**
 * Update a user_data object on user
 */
export function usePatchUserData() {
  return useMutation({
    mutationFn: ({ uuid, params }: { uuid: string; params: UserData }) => patchUserData(uuid, params),
    onError: () => displayGenericErrorToaster(),
    onSettled: () => useGlobalStore().setLoading(false)
  });
}

/* POST */
/**
 * Send a reminder to the user to retake test(s) after 1 year using POST API
 *
 */
export function usePostUserRetakeTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uuid,
      testToRetake,
      isPoleEmploi = false
    }: {
      uuid: string;
      testToRetake: string[];
      isPoleEmploi?: boolean;
      noToaster?: boolean;
    }) => postUserRetakeTest(uuid, testToRetake, isPoleEmploi),
    onSuccess: (_response, context) => {
      // Invalidate user assessment query
      queryClient.invalidateQueries({ queryKey: ['getAssessmentSummary', { uuid: context.uuid }] });
      if (context.isPoleEmploi) {
        queryClient.invalidateQueries({ queryKey: ['getSynthesisPoleEmploi'] });
      }
    },
    onError: (_error, ctx) => {
      if (!ctx?.noToaster) {
        displayGenericErrorToaster();
      }
    }
  });
}

export function usePostRequestVoiceUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userUuid, pmUuid }: { userUuid: string; pmUuid: string }) =>
      postRequestUserPmVoice(userUuid, pmUuid),
    onSuccess: (_res, ctx) => {
      queryClient.invalidateQueries({ queryKey: ['getAssessmentSummary', { uuid: ctx.userUuid }] });
      queryClient.invalidateQueries({ queryKey: ['userSkillsList', { userUuid: ctx.userUuid }] });

      displayGenericSuccessToaster(t('general.invitation.sent', { invitation: 1 }));
    },
    onError: () => displayGenericErrorToaster()
  });
}

export function usePostRestartVoiceUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userUuid, pmUuid }: { userUuid: string; pmUuid: string; noToaster?: boolean }) =>
      postRequestRestartUserPmVoice(userUuid, pmUuid),
    onSuccess: (_res, ctx) => {
      queryClient.invalidateQueries({ queryKey: ['getAssessmentSummary', { uuid: ctx.userUuid }] });

      if (!ctx?.noToaster) {
        displayGenericSuccessToaster(t('general.invitation.sent', { invitation: 1 }));
      }
    },
    onError: (_error, ctx) => {
      if (!ctx?.noToaster) {
        displayGenericErrorToaster();
      }
    }
  });
}

export function usePostRetakeVoiceUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userUuid, pmUuid }: { userUuid: string; pmUuid: string; noToaster?: boolean }) =>
      postRetakeUserPmVoice(userUuid, pmUuid),
    onSuccess: (_res, ctx) => {
      queryClient.invalidateQueries({ queryKey: ['getAssessmentSummary', { uuid: ctx.userUuid }] });
      if (!ctx?.noToaster) {
        displayGenericSuccessToaster(t('general.invitation.sent', { invitation: 1 }));
      }
    },
    onError: (_error, ctx) => {
      if (!ctx?.noToaster) {
        displayGenericErrorToaster();
      }
    }
  });
}

/**
 * Send users results by email
 *
 */
export function usePostSendUserResults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportIds,
      userUuids,
      targetUuid,
      globalParams,
      customReport
    }: {
      reportIds: number[];
      userUuids: string[];
      targetUuid?: string;
      globalParams?: ContactsQueryParams;
      totalCount?: number;
      fullname?: string | null;
      customReport?: { report_custom_hr?: boolean; report_custom_business?: boolean; report_custom_client?: boolean };
    }) => postUserResults(userUuids, reportIds, targetUuid, globalParams, customReport),
    onSuccess: (res, ctx) => {
      handleBatchResponse({
        response: res,
        successToaster: t('recruiter_network.table.action.success.assessment', {
          user: ctx.totalCount || ctx.userUuids.length,
          user_fullname: ctx.fullname
        }),
        nb_actions: ctx.totalCount || ctx.userUuids.length,
        queryClient
      });
    }
  });
}

export function usePostSendWorkflowReminder() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      uuids,
      globalParams,
      type = 'network',
      targetUuid = undefined,
      sourceUuid = undefined
    }: {
      uuids: string[];
      globalParams?:
        | ContactsQueryParams
        | (Partial<CampaignUsersQueryParams> & { step_uuid: string; campaign_uuid: string })
        | undefined;
      totalCount?: number;
      candidate?: BaseUser;
      origin?: string;
      type?: 'network' | 'campaign';
      reporterParams?: Record<string, any>;
      targetUuid?: string;
      sourceUuid?: string;
      successToaster?: string;
    }) => postUserWorkflowReminder(uuids, type, globalParams, targetUuid, sourceUuid),
    onSuccess: (response, ctx) => {
      const hasOnlyCooldownErrors =
        Array.isArray(response) &&
        response.length > 0 &&
        response.every(result => 'status' in result && result.status === HttpStatus.CONFLICT);

      if (hasOnlyCooldownErrors) {
        toast.warning(t('recruiter_profile.reminder.already_sent.description'));
        return;
      }

      // REPORT
      Reporter().reportData('reminder_sent', {
        origin: ctx.origin
      });
      handleBatchResponse({
        response,
        successToaster:
          ctx.successToaster ||
          t('recruiter_network.table.action.success.remind', {
            user_fullname: ctx.candidate ? getFullnameOrEmail(ctx.candidate) : null,
            reminder: ctx.totalCount || ctx.uuids.length
          }),
        nb_actions: ctx.totalCount || ctx.uuids.length,
        callback: () => {
          queryClient.invalidateQueries({ queryKey: ['contactsList'] });
        },
        queryClient
      });
    },
    onError: (error: { status: number }) => {
      if (error.status === HttpStatus.CONFLICT) {
        toast.warning(t('recruiter_profile.reminder.already_sent.description'));
      } else {
        displayGenericErrorToaster();
      }
    }
  });
}
